import { LIMITS, parseImportFile, type BoardSnapshot, type ImportPreview } from '@blueprint/domain'
import { legacyBoardFixture } from '@blueprint/domain/fixtures'
import { useRef, useState } from 'react'
import { Dialog } from '../../components/Dialog.js'
import { Button, Field, TextInput } from '../../components/controls.js'
import { useBoardStore } from '../../state/board-store.js'
import { useUiStore } from '../../state/ui-store.js'
import styles from './ImportDialog.module.css'

interface Staged {
  preview: ImportPreview
  snapshot: BoardSnapshot
}

const MAX_MB = LIMITS.importBytes / (1024 * 1024)

/**
 * Reads a board exported from Blueprint (Export JSON…), or migrates a legacy board export.
 * It previews what will be created, lists everything it could not carry across, and
 * creates a new board on confirmation — it never overwrites an existing one.
 */
export function ImportDialog() {
  const open = useUiStore((state) => state.importOpen)
  const setOpen = useUiStore((state) => state.setImportOpen)
  const importSnapshot = useBoardStore((state) => state.importSnapshot)

  const [staged, setStaged] = useState<Staged | null>(null)
  const [errors, setErrors] = useState<string[]>([])
  const [warnings, setWarnings] = useState<string[]>([])
  const [fileName, setFileName] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const reset = () => {
    setStaged(null)
    setErrors([])
    setWarnings([])
    setFileName(null)
    setName('')
    if (fileRef.current) fileRef.current.value = ''
  }

  const close = () => {
    reset()
    setOpen(false)
  }

  const stage = (text: string, label: string) => {
    setFileName(label)
    const result = parseImportFile(text)
    if (!result.ok) {
      setStaged(null)
      setErrors(result.errors)
      setWarnings(result.warnings)
      return
    }
    setStaged({ preview: result.preview, snapshot: result.snapshot })
    setErrors([])
    setWarnings(result.preview.warnings)
    setName(result.preview.name)
  }

  const onFile = async (file: File) => {
    if (file.size > LIMITS.importBytes) {
      setStaged(null)
      setErrors([`"${file.name}" is larger than ${MAX_MB} MB.`])
      setFileName(file.name)
      return
    }
    try {
      stage(await file.text(), file.name)
    } catch (error) {
      setStaged(null)
      setErrors([`The file could not be read: ${(error as Error).message}`])
    }
  }

  const confirm = async () => {
    if (!staged) return
    setBusy(true)
    const finalName = name.trim() || staged.preview.name
    const created = await importSnapshot({
      ...staged.snapshot,
      board: { ...staged.snapshot.board, name: finalName },
    })
    setBusy(false)
    if (created) close()
  }

  return (
    <Dialog
      open={open}
      wide
      title="Import a board"
      onClose={close}
      onSubmit={confirm}
      footer={
        <>
          <Button onClick={close}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!staged || busy}>
            {busy ? 'Creating…' : 'Create board'}
          </Button>
        </>
      }
    >
      <div className={styles.section}>
        <p style={{ margin: 0, color: 'var(--muted)' }}>
          Reads a board exported from Blueprint with Export JSON…, a JSON file containing a legacy board, or{' '}
          <code>{'{ "name": …, "board": … }'}</code>. A new board is
          created; nothing existing is changed. Limits: {MAX_MB} MB and {LIMITS.importCards.toLocaleString()} cards.
        </p>

        <div className={styles.row}>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            data-autofocus
            aria-label="Board JSON file"
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (file) void onFile(file)
            }}
          />
          <Button onClick={() => stage(JSON.stringify(legacyBoardFixture), 'legacy-board.json (bundled sample)')}>
            Use the bundled sample
          </Button>
        </div>

        {fileName ? <p className={styles.fileName}>Read {fileName}</p> : null}

        {errors.length > 0 ? (
          <div className={`${styles.notice} ${styles.errors}`} role="alert">
            <strong>This file cannot be imported.</strong>
            <ul className={styles.list}>
              {errors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {staged ? (
          <>
            <Field label="Board name">
              {(id) => (
                <TextInput id={id} value={name} maxLength={LIMITS.title} onChange={(event) => setName(event.target.value)} />
              )}
            </Field>

            <div className={styles.counts}>
              <div className={styles.count}>
                <strong>{staged.preview.counts.columns}</strong>
                <span>Columns</span>
              </div>
              <div className={styles.count}>
                <strong>{staged.preview.counts.columnGroups}</strong>
                <span>Groups</span>
              </div>
              <div className={styles.count}>
                <strong>{staged.preview.counts.swimlanes}</strong>
                <span>Swimlanes</span>
              </div>
              <div className={styles.count}>
                <strong>{staged.preview.counts.rows}</strong>
                <span>Rows</span>
              </div>
              <div className={styles.count}>
                <strong>{staged.preview.counts.cards}</strong>
                <span>Cards</span>
              </div>
            </div>
          </>
        ) : null}

        {warnings.length > 0 ? (
          <div className={`${styles.notice} ${styles.warnings}`}>
            <strong>
              {warnings.length} thing{warnings.length === 1 ? '' : 's'} could not be carried across exactly:
            </strong>
            <ul className={styles.list}>
              {warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </Dialog>
  )
}
